import { FormattedFloatInput, Select, SelectMultiple } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";

/** One selectable value of a named product filter, from the catalogue facet. */
export type NamedFilterOption = { value: string; label: string };

/**
 * The named product filters are bare clauses — brand, supplier and the rest
 * carry no property key — so each has its own small control rather than the
 * property-slot-shaped one. Values come from the availability facet, which is
 * the right source here: what matters is the brands and suppliers that exist.
 */
interface Props {
  label: string;
  disabled?: boolean;
}

export function NamedMultiSelectFilterInput({
  label,
  disabled,
  options,
  value,
  onChange,
  getLabelForValue,
}: Props & {
  options: NamedFilterOption[];
  value: string[];
  onChange: (values: string[]) => Promise<void>;
  /** Label for a stored value the facet does not list; defaults to the value. */
  getLabelForValue?: (value: string) => string | undefined;
}) {
  const { t } = useTranslate(["Global"]);
  // A stored value the facet no longer offers — a brand that has left the
  // catalogue, or a value set before the facet loaded — must still render, or
  // the row shows a blank chip and looks broken.
  const optionsWithSelected = [
    ...options,
    ...value
      .filter(v => !options.some(o => o.value === v))
      .map(v => ({ value: v, label: getLabelForValue?.(v) ?? v })),
  ];

  // The same control the property filter rows use, so two filters side by side
  // read as the same kind of thing. An empty facet — the catalogue holds no
  // products of this product type — states itself in the label and disables
  // the control, rather than opening onto nothing.
  const isEmpty = optionsWithSelected.length === 0;
  return (
    <SelectMultiple
      label={label}
      emptyLabel={
        isEmpty ? t("There are no items to display", { ns: "Global" }) : label
      }
      options={optionsWithSelected}
      value={value}
      disabled={disabled || isEmpty}
      onChange={values => void onChange(values)}
    />
  );
}

export function NamedBoolFilterInput({
  label,
  disabled,
  value,
  onChange,
}: Props & {
  value: boolean | null;
  onChange: (value: boolean | null) => Promise<void>;
}) {
  const { t } = useTranslate(["Global"]);
  return (
    <Select
      label={label}
      options={[
        { label: t("Yes", { ns: "Global" }), value: "true" },
        { label: t("No", { ns: "Global" }), value: "false" },
      ]}
      value={value === null ? "" : String(value)}
      disabled={disabled}
      clearable
      onClear={() => void onChange(null)}
      onChange={next =>
        void onChange(next === "true" ? true : next === "false" ? false : null)
      }
    />
  );
}

/**
 * A min/max pair, which is what a `between` clause is. Leaving one side empty
 * is how the row degrades to a one-sided comparison.
 */
export function NamedRangeFilterInput({
  label,
  disabled,
  min,
  max,
  onChange,
}: Props & {
  min: number | null;
  max: number | null;
  onChange: (range: {
    min: number | null;
    max: number | null;
  }) => Promise<void>;
}) {
  const { t } = useTranslate(["Global"]);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <FormattedFloatInput
        label={`${label} (${t("Min", { ns: "Global" })})`}
        value={min}
        disabled={disabled}
        onChange={value => void onChange({ min: value, max })}
      />
      <FormattedFloatInput
        label={`${label} (${t("Max", { ns: "Global" })})`}
        value={max}
        disabled={disabled}
        onChange={value => void onChange({ min, max: value })}
      />
    </Stack>
  );
}
